/**
 * TanStack Query hooks for `bulk-campaign-actions` (FS-814). Thin wrappers
 * over ./api. Invalidates both the jobs list and campaigns list after a
 * successful run because actions mutate campaign state.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	bulkCampaignActionsQueryKeys,
	getJob,
	listCampaigns,
	listJobs,
	runBulkAction,
	type BulkJobView,
	type CampaignListResponse,
	type ListJobsResponse,
	type RunBulkActionBody,
	type RunBulkActionResponse,
} from "./api";

export function useBulkJobsList(): UseQueryResult<ListJobsResponse, Error> {
	return useQuery({
		queryKey: bulkCampaignActionsQueryKeys.jobs(),
		queryFn: listJobs,
	});
}

export function useBulkJob(
	jobId: string | null,
): UseQueryResult<{ job: BulkJobView }, Error> {
	return useQuery({
		queryKey: bulkCampaignActionsQueryKeys.job(jobId ?? ""),
		queryFn: () => getJob(jobId ?? ""),
		enabled: Boolean(jobId),
	});
}

export function useCampaignsList(): UseQueryResult<
	CampaignListResponse,
	Error
> {
	return useQuery({
		queryKey: bulkCampaignActionsQueryKeys.campaigns(),
		queryFn: listCampaigns,
	});
}

export function useRunBulkAction(): UseMutationResult<
	RunBulkActionResponse,
	Error,
	RunBulkActionBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: runBulkAction,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: bulkCampaignActionsQueryKeys.jobs(),
			});
			qc.invalidateQueries({
				queryKey: bulkCampaignActionsQueryKeys.campaigns(),
			});
		},
	});
}
